<template>
    <div class="email-validation-container">
        <div class="pre-requisites-container pt-0">
            <div class="step-label active">Begin Identity Verification</div>
            <h1 class="header">Let's get started</h1>
            <h2 class="sub-header pt-0">Be prepared with:</h2>

            <ul class="pre-requisite-list">
                <li class="desktop-view">
                    <div class="list mr-3">1</div>
                    <span class="pre-requisite-details">A personal mobile device</span>
                </li>
                <li>
                    <div class="list mr-3 desktop-view">2</div>
                    <div class="list mr-3 mobile-view">1</div>
                    <span class="pre-requisite-details">A valid <a class="link"
                            href="https://getnametag.com/docs/govtid-doctypes/" target="_blank">government-issued photo ID</a>
                        (originals only)</span>
                </li>
                <li>
                    <div class="list mr-3 desktop-view">3</div>
                    <div class="list mr-3 mobile-view">2</div>
                    <span class="pre-requisite-details">A stable internet connection</span>
                </li>
            </ul>
        </div>
        <div class="email-container">
            <label for="email" class="email-label">Enter your Autodesk email address</label>
            <input type="email" id="email" v-model="email" class="email-input" placeholder="mailid@autodesk.com"
                @input="validateEmail" />

            <span v-if="emailError" class="error-message">Please provide a valid Autodesk email address.</span>

            <div class="checkbox-container mb-0">
                <input type="checkbox" id="consent" v-model="consent" class="custom-checkbox"
                    @change="isConsentGiven" />
                <div class="checkbox-label text-justify ml-1" v-html="userDataUsageConsent"></div>
            </div>

            <div class="d-flex justify-content-end button-container">
                <button class="button cancel-button mt-3 mr-3 cursor-pointer" @click="cancelStep">Cancel</button>
                <button class="button next-button mt-3 cursor-pointer" :disabled="!isEmailAndConsentValid"
                    @click="nextStep">Next</button>
            </div>
            <div class="mobile-stepper">
                <IdentityVerificationMobileStepper :currentStep="0" />
            </div>
            <loading-overlay v-if="isLoading"></loading-overlay>
        </div>
    </div>
</template>

<script>
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import IdentityVerificationMobileStepper from "@/components/IdentityVerificationMobileStepper.vue";
import { createNametagVerificationRequest } from '@/api/index.js';

export default {
    name: "BeginIdentityVerification",
    components: {
        LoadingOverlay,
        IdentityVerificationMobileStepper
    },
    props: {
        userDataUsageConsent: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            email: "",
            consent: false,
            emailError: false,
            isEmailAndConsentValid: false,
            isLoading: false
        };
    },
    methods: {
        validateEmail() {
            const regex = /^[a-zA-Z0-9._%+-]+@autodesk\.com$/;
            this.emailError = !regex.test(this.email);
            this.isConsentGiven();
        },
        cancelStep() {
            document.title = 'Enterprise Search';
            this.$router.push('/');
        },
        isConsentGiven() {
            this.isEmailAndConsentValid = !this.emailError && this.consent && this.email.length > 0;
        },
        async nextStep() {
            if (this.isEmailAndConsentValid) {
                this.isLoading = true;
                try {
                    const response = await createNametagVerificationRequest(this.email);
                    const data = await response.json();
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                    this.$emit("nextStep", data, this.email);
                } catch (error) {
                    this.emailError = "Server Error!! Please contact Service Desk for more details"
                } finally {
                    this.isLoading = false;
                }
            }
        },
    }
};
</script>

<style scoped>
.email-validation-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;
    height: auto;
    max-width: 1200px;
    color: black;
    margin-top: 30px;
}

.pre-requisites-container {
    width: 50%;
    height: 100%;
    padding: 30px 10px;
}

.email-container {
    width: 50%;
    height: 100%;
    padding: 20px 30px;
    border-left: 1px solid gray;
}

.header {
    font-size: 16px;
    font-weight: 700;
    color: black;
    font-family: artifaktElementBold;
    margin: 0px;
    margin-bottom: 20px;
    padding-top: 20px;
}

.sub-header {
    font-size: 15px;
    font-weight: 800;
    color: black;
    font-family: artifaktElementRegular;
    margin: 0px;
    margin-bottom: 20px;
}

.mobile-view {
    display: none !important;
}

.desktop-view {
    display: flex !important;
}

.list {
    width: 18px;
    height: 18px;
    font-size: 12px;
    border: 1.5px solid gray;
    border-radius: 3px;
    text-align: center;
    font-family: artifaktElementBold;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 3px !important;
}

.custom-checkbox {
    height: 18px;
    width: 18px;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 3px;
    margin-right: 8px;
    cursor: pointer;
    margin-top: 4px;
    position: relative;
}

.custom-checkbox:checked {
    background-color: #5f60ff;
    border-color: #5f60ff;
}

.custom-checkbox:checked::before {
    content: '';
    position: absolute;
    width: 6px;
    height: 13px;
    border-right: 3px solid white;
    border-bottom: 3px solid white;
    top: 1px;
    left: 6px;
    transform: rotate(40deg);
}

.mobile-stepper {
    display: none;
}

.step-label {
    display: none;
    font-weight: normal;
    font-size: 16px;
    margin-top: 3px;
    margin-bottom: 20px;
    font-weight: 700;
}

.pre-requisite-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
}

.pre-requisite-list li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    text-align: center;
}

.icon {
    margin-right: 10px;
    width: 20px;
    height: 20px;
}

.pre-requisite-details {
    text-align: left;
    font-size: 16px;
    font-weight: 600;
    color: black;
    font-family: artifaktElementRegular;
    width: 80%;
}

.email-label {
    font-weight: bold;
    margin-bottom: 8px;
    color: black;
    font-family: artifaktElementBold;
}

.email-input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
}

.error-message {
    color: red;
    font-size: 12px;
    margin-bottom: 8px;
}

.button {
    border-radius: 40px;
    font-weight: 700;
    font-size: 14px;
    width: 130px;
    height: 36px;
}

.cancel-button {
    background-color: white;
    color: #5f60ff;
    padding: 6px 40px;
    border: 2px solid #5f60ff;
}

.next-button {
    background-color: #5f60ff;
    color: white;
    padding: 6px 40px;
    border: none;
}

.next-button:hover:not(:disabled) {
    background-color: #5a52d1;
}

.next-button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.checkbox-container {
    display: flex;
    margin: 20px 0px;
}

.checkbox-label {
    font-size: 14px;
    width: 90%;
}

.link {
    color: #5f60ff;
    text-decoration: none;
    word-break: break-all;
}

.link:hover {
    text-decoration: underline;
}

@media (max-width: 700px) {
    .email-validation-container {
        flex-direction: column;
        border-left: 2px solid lightgray;
        padding-left: 20px;
        margin-left: 14px;
        margin-top: -30px !important;
    }

    .pre-requisites-container {
        width: 100%;
        padding: 0px;
    }

    .pre-requisite-details {
        text-align: start;
    }

    .email-container {
        width: 90%;
        padding: 0px;
        border-left: none;
    }

    .mobile-view {
        display: flex !important;
    }

    .desktop-view {
        display: none !important;
    }

    .mobile-stepper {
        display: block;
    }

    .step-label {
        display: flex !important;
        font-weight: 900;
        white-space: nowrap;
        width: auto;
        font-size: 16px;
        margin-bottom: 0px !important;
        margin-left: 4px !important;
    }

    .pre-requisite-list li {
        margin-bottom: 20px;
        align-items: flex-start;
        text-align: start;
    }

    .button-container {
        justify-content: center !important;
    }
}
</style>